<script setup lang="ts">
/**
 * 客户悬浮聊天窗（plain Vue，非 Vben）。
 *
 * 接线口径：
 * - 只消费既有接口：{@link WidgetSession} 的身份/收发/状态 + {@link WidgetBridge} 的宿主协议，
 *   本组件不新建身份体系、不直连 HTTP；
 * - 消息只来自 session.visibleMessages()（客户视角已排除内部备注）；
 * - 发送/重试都走 session（同 key 重试复用同一 clientMessageId 与同一上下文快照）；
 * - 发送状态由待发队列决定，已读只使用服务端返回的客服已读游标。
 */

import type { Message, PendingSend, SupportAttachment } from '@support/client';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import type { WidgetSession, WidgetSessionState } from './session';
import DefaultAvatar from './DefaultAvatar.vue';
import MarkdownMessage from './MarkdownMessage.vue';
import { prefersInstantText, SmoothText } from './smooth-text';
import AiAvatar from '../../shared/AiAvatar.vue';
import ImageAttachmentPreview from '../../shared/ImageAttachmentPreview.vue';

const props = defineProps<{ session: WidgetSession }>();

const messages = ref<Message[]>([]);
const customerAvatarSeed = ref('customer::');
const historyBoundary = ref('0');
let openingBoundaryPending = false;
const HISTORY_GAP_MS = 60 * 60 * 1000;

function laterSequence(left: string, right: string): string {
  if (!/^\d+$/.test(left)) return right;
  if (!/^\d+$/.test(right)) return left;
  return BigInt(left) >= BigInt(right) ? left : right;
}

function isHistoryBoundary(message: Message, index: number): boolean {
  const sequence = message.publicSequence;
  if (!sequence || !/^\d+$/.test(sequence) || historyBoundary.value === '0') return false;
  if (BigInt(sequence) > BigInt(historyBoundary.value)) return false;
  const next = messages.value[index + 1]?.publicSequence;
  return !next || (/^\d+$/.test(next) && BigInt(next) > BigInt(historyBoundary.value));
}
const pending = ref<PendingSend[]>([]);
const draft = ref('');
const errorKind = ref<undefined | string>();
const errorMessage = ref<undefined | string>();
const messageList = ref<HTMLElement | null>(null);
const open = ref(false);
const conversationStatus = ref<null | 'active' | 'ai' | 'closed' | 'waiting'>(null);
const handoffBusy = ref(false);
const handoffError = ref('');
const resolutionChoice = ref<'resolved' | 'unresolved' | null>(null);
const newConversationBusy = ref(false);
const state = ref<WidgetSessionState>('idle');
const unread = ref(0);
const staffReadCursor = ref('0');
const counterpartTyping = ref(false);
const aiResponding = ref(false);
const aiStatus = ref('');
const answerPreview = ref('');
/** 匀速显示后的预览正文；流结束后保留到最终消息接上，避免气泡先消失再出现。 */
const previewShown = ref('');
/** 正在接续显示的最终消息 id：它的正文暂由 previewShown 提供，追平后回到消息自身。 */
const handoverId = ref('');
const previewVisible = computed(() => previewShown.value !== '' && handoverId.value === '');
let followLatest = true;
const queueCount = ref(0);
const allAgentsFull = ref(false);
const queuePosition = ref(0);
/** 满意度评价：hidden 不显示；pending 待评价；rated 已评价。 */
const ratingState = ref<'hidden' | 'pending' | 'rated'>('hidden');
const ratingScore = ref(0);
const ratingComment = ref('');
const ratingBusy = ref(false);
const ratingError = ref('');
const ratingJustSubmitted = ref(false);
let ratingKey = '';
const estimatedWaitSeconds = ref(0);
const offHours = ref(false);
const serviceHours = ref('');
const selectedFiles = ref<File[]>([]);
const attachmentError = ref<string | null>(null);
const uploading = ref(false);
const downloadUrls = new Set<string>();
const uploadedAttachments = new Map<File, { id: string; visibility?: string }>();
let identityKey = '';
let generation = 0;

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
/** 与后端白名单一致；不支持的类型在选择时直接提示，而不是上传后才失败。 */
const ATTACHMENT_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.pdf', '.txt', '.xlsx', '.docx', '.zip'];
const ATTACHMENT_ACCEPT = ATTACHMENT_EXTENSIONS.join(',');

const unsubscribes: (() => void)[] = [];

const STATE_LABELS: Record<WidgetSessionState, string> = {
  authenticated: '已连接客服',
  authenticating: '正在连接客服…',
  error: '连接异常',
  idle: '等待连接…',
  unauthenticated: '未登录',
};

const ERROR_TITLES: Record<string, string> = {
  auth: '登录状态已过期',
  capacity: '客服繁忙',
  conflict: '操作冲突，请重试',
  forbidden: '无权访问客服会话',
  identity: '身份校验未通过',
  internal: '服务端错误',
  'not-found': '会话不存在',
  network: '网络异常',
  protocol: '协议错误',
  realtime: '实时连接不可用',
  sync: '消息同步失败',
  timeout: '请求超时',
  unauthorized: '无权访问客服会话',
};

const stateLabel = computed(() =>
  state.value === 'authenticated' && conversationStatus.value === 'closed'
    ? '会话已结束'
    : state.value === 'authenticated' && conversationStatus.value === 'ai'
    ? '智能客服接待中'
    : state.value === 'authenticated' && conversationStatus.value === 'waiting'
      ? '等待人工客服'
      : STATE_LABELS[state.value],
);

/** 排队提示：非工作时间说明工作时间；没有空闲坐席（暂停、停用、离线或满载）时显示排队位置与预计等待；否则提示正在接入。 */
const queueNotice = computed(() => {
  if (state.value !== 'authenticated' || conversationStatus.value !== 'waiting' || queueCount.value <= 0) return '';
  if (offHours.value) {
    const hours = serviceHours.value ? `（人工客服工作时间 ${serviceHours.value}）` : '';
    return `现在是非工作时间${hours}，客服上班后会尽快回复您，您也可以继续向智能客服提问。`;
  }
  if (allAgentsFull.value) {
    const ahead = queuePosition.value > 1 ? `您前面还有 ${queuePosition.value - 1} 位，` : '';
    const minutes = estimatedWaitSeconds.value > 0 ? `预计等待约 ${Math.max(1, Math.round(estimatedWaitSeconds.value / 60))} 分钟，` : '';
    return `暂无空闲的人工客服，${ahead}${minutes}我们会按顺序为您接入。`;
  }
  return '正在为您接入人工客服，请稍候。';
});

/** 最新一条智能客服答复的追问建议；仍由智能客服接待且没有未完成的提问时才显示。 */
const suggestions = computed(() => {
  if (state.value !== 'authenticated' || (conversationStatus.value !== 'ai' && conversationStatus.value !== 'waiting')) return [];
  if (pending.value.length > 0 || aiResponding.value || previewShown.value || handoverId.value) return [];
  const latest = messages.value.at(-1);
  if (!latest || !isAIMessage(latest) || !Array.isArray(latest.suggestions)) return [];
  return latest.suggestions.filter((item) => typeof item === 'string' && item.trim() !== '').slice(0, 3);
});

/** 仅在本轮 AI 答复完成后确认解决情况；排队、人工接待和旧轮次不显示。 */
const resolutionKey = computed(() => {
  if (state.value !== 'authenticated' || conversationStatus.value !== 'ai' ||
      pending.value.length > 0 || aiResponding.value || previewShown.value || handoverId.value) return '';
  const latest = messages.value.at(-1);
  if (!latest || !isAIMessage(latest)) return '';
  const sequence = latest.publicSequence ?? latest.sequence;
  const boundary = props.session.historyBeforeSequence;
  if (/^\d+$/.test(sequence) && /^\d+$/.test(boundary) && BigInt(sequence) <= BigInt(boundary)) return '';
  return `${customerAvatarSeed.value}:${props.session.conversationId}:${boundary}:${latest.id}`;
});

watch(resolutionKey, () => {
  resolutionChoice.value = null;
  handoffError.value = '';
}, { flush: 'sync' });

/** 点选追问建议即作为客户消息发送。 */
function sendSuggestion(text: string): void {
  if (waitingForAI.value || uploading.value) return;
  draft.value = text;
  props.session.setDraft(text);
  void onSend();
}

/** AI 接待（含排队期间）上一条尚未回复完时不能发新消息，避免问题被覆盖。 */
const waitingForAI = computed(() =>
  (conversationStatus.value === 'ai' || conversationStatus.value === 'waiting') &&
  (aiResponding.value || pending.value.some((item) => item.status === 'sending')));
const errorTitle = computed(() => ERROR_TITLES[errorKind.value ?? ''] ?? '发生错误');

function refresh(): void {
  const session = props.session;
  const nextIdentityKey = `${session.principal?.id ?? ''}:${session.conversationId ?? ''}`;
  if (identityKey !== nextIdentityKey) {
    identityKey = nextIdentityKey;
    historyBoundary.value = '0';
    generation += 1;
    followLatest = true;
    selectedFiles.value = [];
    uploadedAttachments.clear();
    attachmentError.value = null;
    uploading.value = false;
    awaitingReply = false;
    endPreview();
  }
  messages.value = session.visibleMessages();
  customerAvatarSeed.value = `customer:${session.principal?.orgId ?? ''}:${session.principal?.id ?? ''}`;
  historyBoundary.value = laterSequence(historyBoundary.value, session.historyBeforeSequence);
  if (!open.value && session.isOpen) openingBoundaryPending = true;
  if (openingBoundaryPending && session.state === 'authenticated' && messages.value.length > 0) {
    const latest = messages.value.at(-1);
    const receivedAt = latest ? Date.parse(latest.receivedAt) : NaN;
    if (latest?.publicSequence && Number.isFinite(receivedAt) && Date.now() - receivedAt > HISTORY_GAP_MS) {
      historyBoundary.value = laterSequence(historyBoundary.value, latest.publicSequence);
    }
    openingBoundaryPending = false;
  }
  pending.value = session.pendingSends();
  const status = session.status;
  state.value = session.state;
  errorKind.value = status.errorKind;
  errorMessage.value = status.errorMessage;
  open.value = session.isOpen;
  conversationStatus.value = session.conversationStatus;
  unread.value = status.unread ?? 0;
  staffReadCursor.value = session.staffReadCursor;
  counterpartTyping.value = session.counterpartTyping;
  aiResponding.value = session.aiResponding;
  aiStatus.value = session.aiStatus;
  answerPreview.value = session.answerPreview;
  queueCount.value = session.queueCount;
  allAgentsFull.value = session.allAgentsFull;
  queuePosition.value = session.queuePosition;
  estimatedWaitSeconds.value = session.estimatedWaitSeconds;
  offHours.value = session.offHours;
  serviceHours.value = session.serviceHours;
  void syncRating();
  // 身份切换会清空会话草稿：只在会话层草稿变空时同步，避免覆盖用户正在输入的内容
  if (session.draft === '' && draft.value !== '') draft.value = '';
}

/** 会话结束时读取本轮评价；继续咨询后隐藏评价卡片。 */
async function syncRating(): Promise<void> {
  const key = `${identityKey}:${conversationStatus.value}:${historyBoundary.value}`;
  if (conversationStatus.value !== 'closed' || state.value !== 'authenticated') {
    ratingState.value = 'hidden';
    ratingKey = '';
    return;
  }
  if (ratingKey === key) return;
  ratingKey = key;
  ratingJustSubmitted.value = false;
  ratingScore.value = 0;
  ratingComment.value = '';
  ratingError.value = '';
  try {
    const rating = await props.session.loadRating();
    if (ratingKey !== key || !rating) return;
    ratingState.value = rating.rated ? 'rated' : 'pending';
    if (rating.rated) ratingScore.value = rating.score;
  } catch {
    // 评价是附加功能，读取失败时不打扰客户。
    if (ratingKey === key) ratingState.value = 'hidden';
  }
}

async function submitRating(): Promise<void> {
  if (ratingBusy.value || ratingScore.value < 1) return;
  ratingBusy.value = true;
  ratingError.value = '';
  try {
    const rating = await props.session.submitRating(ratingScore.value, ratingComment.value.trim());
    ratingState.value = 'rated';
    ratingScore.value = rating.score;
    ratingJustSubmitted.value = true;
  } catch {
    ratingError.value = '评价提交失败，请稍后重试';
  } finally {
    ratingBusy.value = false;
  }
}

/** 点选解决情况并上报给主管复盘；上报失败不影响客户继续咨询或转人工。 */
function chooseResolution(choice: 'resolved' | 'unresolved'): void {
  if (resolutionChoice.value === choice) return;
  resolutionChoice.value = choice;
  const latest = messages.value.at(-1);
  if (!latest || !isAIMessage(latest)) return;
  props.session.submitAIFeedback(latest.id, choice === 'resolved').catch(() => {});
}

/** 客户在窗口里的任何操作都算作对话：重新计算空闲时间，已因长时间无对话暂停的连接立即恢复。 */
function noteActivity(): void {
  props.session.noteActivity();
}

function onInput(event: Event): void {
  const value = (event.target as HTMLTextAreaElement).value;
  draft.value = value;
  props.session.setDraft(value);
}

async function onHandoff(): Promise<void> {
  if (handoffBusy.value || !resolutionKey.value || resolutionChoice.value !== 'unresolved') return;
  const gate = generation;
  const key = resolutionKey.value;
  handoffBusy.value = true;
  handoffError.value = '';
  try {
    await props.session.requestHuman();
  } catch {
    if (gate === generation && resolutionKey.value === key) handoffError.value = '转人工失败，请稍后重试';
  } finally {
    handoffBusy.value = false;
  }
}

async function onNewConversation(): Promise<void> {
  if (newConversationBusy.value) return;
  newConversationBusy.value = true;
  try {
    await props.session.startNewConversation();
    attachmentError.value = null;
  } catch {
    attachmentError.value = '新咨询发起失败，请稍后重试';
  } finally {
    newConversationBusy.value = false;
  }
}

async function onSend(): Promise<void> {
  const text = draft.value;
  const files = [...selectedFiles.value];
  if ((text.trim() === '' && files.length === 0) || state.value !== 'authenticated' || conversationStatus.value === 'closed' || uploading.value || waitingForAI.value) return;
  const gate = generation;
  const attachments: { id: string; isImage: boolean }[] = [];
  uploading.value = files.length > 0;
  attachmentError.value = null;
  try {
    for (const file of files) {
      let cached = uploadedAttachments.get(file);
      if (!cached) {
        const result = await props.session.uploadAttachment(file);
        if (gate !== generation) return;
        if (typeof result?.id !== 'string' || result.id.length === 0) throw new Error('Invalid attachment');
        cached = { id: result.id };
        uploadedAttachments.set(file, cached);
      }
      attachments.push({ id: cached.id, isImage: file.type.startsWith('image/') });
    }
  } catch {
    if (gate === generation) attachmentError.value = '附件上传失败，请重试';
    return;
  } finally {
    if (gate === generation) uploading.value = false;
  }
  if (gate !== generation || state.value !== 'authenticated') return;
  try {
    props.session.sendMessage(text, attachments);
    draft.value = '';
    props.session.setDraft('');
    selectedFiles.value = [];
    uploadedAttachments.clear();
    attachmentError.value = null;
  } catch {
    // 会话尚未就绪：保留草稿，身份就绪后用户可再发
  }
}

function onPaste(event: ClipboardEvent): void {
  if (state.value !== 'authenticated' || conversationStatus.value === 'closed' || uploading.value || waitingForAI.value) return;
  const items = Array.from(event.clipboardData?.items ?? [])
    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
    .map((item) => item.getAsFile()).filter((file): file is File => file !== null);
  const files = items.length ? items : Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'));
  if (!files.length) return;
  event.preventDefault();
  attachmentError.value = null;
  files.forEach(setSelectedFile);
}

function onFileChange(event: Event): void {
  const input = event.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = '';
  attachmentError.value = null;
  files.forEach(setSelectedFile);
}

function setSelectedFile(file: File): void {
  if (state.value !== 'authenticated' || conversationStatus.value === 'closed' || uploading.value || waitingForAI.value) return;
  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] ?? '';
  if (!ATTACHMENT_EXTENSIONS.includes(extension)) {
    attachmentError.value = '仅支持图片（png/jpg/webp）、PDF、TXT、Excel、Word、ZIP 附件';
    return;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    attachmentError.value = '附件不能超过 10 MB';
    return;
  }
  if (selectedFiles.value.length >= 5) {
    attachmentError.value = '每条消息最多添加 5 个附件（图片最多 5 张）';
    return;
  }
  selectedFiles.value = [...selectedFiles.value, file];
}

function removeFile(file: File): void {
  if (uploading.value) return;
  selectedFiles.value = selectedFiles.value.filter((item) => item !== file);
  uploadedAttachments.delete(file);
  attachmentError.value = null;
}

async function download(message: Message, attachment: SupportAttachment): Promise<void> {
  if (state.value !== 'authenticated' || !messages.value.includes(message) ||
    !message.attachments.some((item) => item.id === attachment.id)) return;
  const gate = generation;
  attachmentError.value = null;
  try {
    const blob = await props.session.downloadAttachment(attachment.id);
    if (gate !== generation) return;
    const url = URL.createObjectURL(blob);
    downloadUrls.add(url);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.name.split(/[\\/]/).pop() || 'attachment';
    link.click();
    const revoke = URL.revokeObjectURL.bind(URL);
    window.setTimeout(() => {
      revoke(url);
      downloadUrls.delete(url);
    }, 30_000);
  } catch {
    if (gate === generation) attachmentError.value = '附件下载失败，请重试';
  }
}

/** 输入法组字时的回车用于确认候选词，不能当作发送。 */
function onComposerEnter(event: KeyboardEvent): void {
  if (event.isComposing || event.keyCode === 229) return;
  event.preventDefault();
  void onSend();
}

function onRetry(clientMessageId: string): void {
  props.session.retrySend(clientMessageId);
}

function onOpen(): void {
  props.session.setOpen(true);
}

function onClose(): void {
  props.session.setOpen(false);
}

function bubbleClass(message: Message): string {
  return message.senderType === 'customer' ? 'widget-bubble--out' : 'widget-bubble--in';
}

function isAIMessage(message: Message): boolean {
  return message.senderType === 'system' && message.senderId === 'ai';
}

/** 系统提示（如转人工通知）居中展示，不当作某一方的聊天气泡。 */
function isSystemNotice(message: Message): boolean {
  return message.senderType === 'system' && message.senderId !== 'ai' && message.attachments.length === 0;
}

function avatarSeed(message: Message): string {
  return message.senderType === 'customer' ? customerAvatarSeed.value : `staff:${message.senderId}`;
}

function formatMessageTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (number: number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** 列表内的简短时间：今天只显示时分，今年显示月日，更早显示完整日期；完整时间见悬停提示。 */
function formatShortTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const pad = (number: number) => String(number).padStart(2, '0');
  const clock = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  if (date.toDateString() === now.toDateString()) return clock;
  if (date.getFullYear() === now.getFullYear()) return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${clock}`;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${clock}`;
}

function isRead(message: Message): boolean {
  if (!message.publicSequence || !/^\d+$/.test(message.publicSequence) || !/^\d+$/.test(staffReadCursor.value)) return false;
  return BigInt(message.publicSequence) <= BigInt(staffReadCursor.value);
}

const smooth = new SmoothText({ onChange: (shown) => { previewShown.value = shown; }, instant: prefersInstantText });
/** 预览开始时已有的消息；之后出现的第一条智能客服消息就是这段预览的最终落库版本。 */
let previewBaseIds = new Set<string>();
let settleTimer: null | ReturnType<typeof setTimeout> = null;
const SETTLE_MS = 1500;

function endPreview(): void {
  if (settleTimer !== null) clearTimeout(settleTimer);
  settleTimer = null;
  handoverId.value = '';
  smooth.reset();
}

/** 流已结束：最终消息一到就接着往下显示，不再另起一个气泡。 */
function tryHandover(): void {
  if (answerPreview.value || !previewShown.value || handoverId.value) return;
  const final = messages.value.find((message) => !previewBaseIds.has(message.id) && isAIMessage(message));
  if (!final) return;
  awaitingReply = false;
  if (settleTimer !== null) clearTimeout(settleTimer);
  settleTimer = null;
  // 服务端去 Markdown 标记后正文可能有细微差别；开头对得上就接着显示，完全不同（如答复被替换）才直接换。
  if (!final.text.startsWith(previewShown.value.slice(0, 10))) {
    endPreview();
    return;
  }
  handoverId.value = final.id;
  smooth.set(final.text);
  if (!smooth.pending) endPreview();
}

/** 客户发问后等待答复的标记：只有它成立时，新到的智能客服消息才按“刚生成”处理。 */
let awaitingReply = false;
watch(aiResponding, (value) => {
  if (value) awaitingReply = true;
});

/**
 * 没有流式预览也要平滑：服务端没推增量（一次性发布、代理缓冲、断流补拉）时，
 * 刚生成的智能客服回复同样从头逐字显示，而不是整段蹦出来。历史消息与旧回复不受影响。
 */
function startLiveReveal(current: Message[], previous: Message[]): void {
  if (!awaitingReply || handoverId.value || previewShown.value || prefersInstantText()) return;
  const latest = current.at(-1);
  if (!latest || !isAIMessage(latest) || !latest.text || previous.some((message) => message.id === latest.id)) return;
  awaitingReply = false;
  // 窗口收起或页面在后台时用户看不到打字过程，直接显示全文，回来时不必重新等一遍。
  if (!open.value || (typeof document !== 'undefined' && document.hidden)) return;
  handoverId.value = latest.id;
  smooth.set(latest.text);
}

watch(answerPreview, (text) => {
  if (text) {
    if (settleTimer !== null) clearTimeout(settleTimer);
    settleTimer = null;
    if (!previewShown.value) previewBaseIds = new Set(messages.value.map((message) => message.id));
    handoverId.value = '';
    smooth.set(text);
    return;
  }
  if (prefersInstantText() || !previewShown.value) {
    endPreview();
    return;
  }
  tryHandover();
  if (!handoverId.value && settleTimer === null) settleTimer = setTimeout(endPreview, SETTLE_MS);
}, { flush: 'sync' });
watch(messages, (current, previous) => {
  tryHandover();
  startLiveReveal(current, previous);
});
watch(previewShown, () => {
  if (handoverId.value && !smooth.pending) endPreview();
});

onMounted(() => {
  unsubscribes.push(props.session.on('status', refresh));
  unsubscribes.push(props.session.on('event', refresh));
  unsubscribes.push(props.session.on('pending', refresh));
  refresh();
});

onBeforeUnmount(() => {
  generation += 1;
  smooth.dispose();
  if (settleTimer !== null) clearTimeout(settleTimer);
  for (const unsubscribe of unsubscribes.splice(0)) unsubscribe();
  for (const url of downloadUrls) URL.revokeObjectURL(url);
  downloadUrls.clear();
});

// 阅读旧消息时保持位置；已在底部时逐步跟随流式正文。
function onMessageScroll(): void {
  const list = messageList.value;
  if (list) followLatest = list.scrollHeight - list.scrollTop - list.clientHeight <= 48;
}
watch([messages, pending, counterpartTyping, aiResponding, previewShown, open, resolutionChoice, ratingState, ratingScore, ratingError], async () => {
  await nextTick();
  const list = messageList.value;
  if (list && followLatest) list.scrollTop = list.scrollHeight;
});
</script>

<template>
  <div
    class="widget-root"
    data-testid="widget-root"
    @pointerdown.capture="noteActivity"
    @keydown.capture="noteActivity"
    @wheel.capture.passive="noteActivity"
    @touchstart.capture.passive="noteActivity"
  >
    <button
      v-if="!open"
      aria-label="打开客服窗口"
      class="widget-launcher"
      type="button"
      @click="onOpen"
    >
      <span aria-hidden="true" class="widget-launcher__icon">💬</span>
      <span class="widget-launcher__text">在线客服</span>
      <span v-if="unread > 0" class="widget-launcher__badge">{{ unread }}</span>
    </button>

    <section v-else aria-label="在线客服" class="widget-panel" data-testid="widget-panel">
      <header class="widget-panel__header">
        <div class="widget-panel__heading">
          <h1 class="widget-panel__title">在线客服</h1>
        </div>
        <div class="widget-panel__meta">
          <span class="widget-panel__status" data-testid="widget-status">{{ stateLabel }}</span>
        </div>
        <button
          aria-label="收起客服窗口"
          class="widget-panel__close"
          type="button"
          @click="onClose"
        >
          收起
        </button>
      </header>

      <p v-if="queueNotice" class="widget-panel__queue" data-testid="widget-queue" role="status">
        {{ queueNotice }}
      </p>
      <div v-if="state === 'authenticated' && conversationStatus === 'closed'" class="widget-panel__closed" data-testid="widget-closed" role="status">
        <span class="widget-panel__closed-text">本次咨询已结束，历史消息仍可查看。</span>
        <button class="widget-panel__continue" type="button" data-testid="widget-new-conversation" :disabled="newConversationBusy" @click="onNewConversation">
          {{ newConversationBusy ? '继续中…' : '继续咨询' }}
        </button>
      </div>

      <p v-if="errorMessage" class="widget-alert" data-testid="widget-error" role="alert">
        <strong class="widget-alert__title">{{ errorTitle }}</strong>
        <span class="widget-alert__message">{{ errorMessage }}</span>
      </p>

      <div
        ref="messageList"
        aria-label="消息记录"
        aria-live="polite"
        class="widget-panel__body"
        data-testid="widget-messages"
        role="log"
        @scroll="onMessageScroll"
      >
        <p v-if="messages.length === 0 && pending.length === 0 && !aiResponding" class="widget-empty" data-testid="widget-empty">
          暂无消息，请描述您遇到的问题。
        </p>

        <template v-for="(message, index) in messages" :key="message.id">
        <p v-if="isSystemNotice(message)" class="widget-notice" data-testid="widget-notice" role="status">
          {{ message.text }}
        </p>
        <article
          v-else
          class="widget-message"
          :class="bubbleClass(message)"
          :data-sender="message.senderType"
          data-testid="widget-message"
        >
          <AiAvatar v-if="isAIMessage(message)" class="widget-message__avatar" />
          <span v-else-if="message.senderType === 'system'" class="widget-message__system-avatar" aria-hidden="true">系</span>
          <DefaultAvatar v-else class="widget-message__avatar" :seed="avatarSeed(message)" />
          <div class="widget-message__content">
            <span v-if="isAIMessage(message)" class="widget-message__sender" data-testid="widget-sender">智能客服</span>
            <div class="widget-bubble" :title="formatMessageTime(message.receivedAt)">
              <MarkdownMessage :source="message.id === handoverId ? previewShown : message.text || (message.contentType === 'image' ? '图片' : message.contentType === 'file' ? '附件' : '')" :legacy-tables="isAIMessage(message)" />
              <ul v-if="message.attachments.length > 0" class="widget-bubble__attachments" aria-label="消息附件" data-testid="widget-attachments">
                <li v-for="attachment in message.attachments" :key="attachment.id">
                  <button type="button" data-testid="widget-attachment-download" @click.stop="download(message, attachment)">
                    下载 {{ attachment.name }}
                  </button>
                </li>
              </ul>
            </div>
            <div class="widget-message__footer">
              <time class="widget-message__time" :datetime="message.receivedAt" :title="formatMessageTime(message.receivedAt)">{{ formatShortTime(message.receivedAt) }}</time>
              <section v-if="resolutionKey && index === messages.length - 1" class="widget-resolution" data-testid="widget-resolution" aria-label="您的问题解决了吗？" aria-live="polite">
                <div class="widget-resolution__actions">
                  <button type="button" class="widget-resolution__choice" data-testid="widget-resolved" aria-label="已解决" title="已解决" :aria-pressed="resolutionChoice === 'resolved'" :disabled="handoffBusy" @click="chooseResolution('resolved')">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10v10H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h3Zm0 0 5-7a3 3 0 0 1 3 3l-1 4h5a2 2 0 0 1 2 2.4l-1.2 6a2 2 0 0 1-2 1.6H7" /></svg>
                  </button>
                  <button type="button" class="widget-resolution__choice" data-testid="widget-unresolved" aria-label="未解决" title="未解决" :aria-pressed="resolutionChoice === 'unresolved'" :disabled="handoffBusy" @click="chooseResolution('unresolved')">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 14V4H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3Zm0 0 5 7a3 3 0 0 0 3-3l-1-4h5a2 2 0 0 0 2-2.4l-1.2-6a2 2 0 0 0-2-1.6H7" /></svg>
                  </button>
                  <span v-if="resolutionChoice === 'resolved'" class="widget-resolution__thanks" data-testid="widget-resolution-done">感谢反馈</span>
                </div>
              </section>
              <span v-if="message.senderType === 'customer' && conversationStatus !== 'ai'" class="widget-message__receipt" :class="{ 'widget-message__receipt--read': isRead(message) }" data-testid="widget-receipt">
                {{ isRead(message) ? '已读' : '未读' }}
              </span>
            </div>
            <div v-if="resolutionKey && index === messages.length - 1 && resolutionChoice === 'unresolved'" class="widget-resolution widget-resolution__handoff">
              <p>您可以继续补充问题，或转接人工客服。</p>
              <button type="button" class="widget-panel__handoff" data-testid="widget-handoff" :disabled="handoffBusy" @click="onHandoff">
                {{ handoffBusy ? '转接中…' : '转人工' }}
              </button>
              <p v-if="handoffError" class="widget-resolution__error" data-testid="widget-handoff-error" role="alert">{{ handoffError }}</p>
            </div>
          </div>
        </article>
        <div v-if="isHistoryBoundary(message, index)" class="widget-history-divider" data-testid="widget-history-divider" role="separator">
          <span>以上为历史消息</span>
        </div>
        </template>

        <article
          v-for="item in pending"
          :key="item.clientMessageId"
          class="widget-message widget-bubble--out widget-bubble--pending"
          :data-status="item.status"
          data-testid="widget-pending"
        >
          <DefaultAvatar class="widget-message__avatar" :seed="customerAvatarSeed" />
          <div class="widget-message__content">
            <div class="widget-bubble">
              <MarkdownMessage :source="item.text || (item.contentType === 'image' ? '图片' : '附件')" />
            </div>
            <div class="widget-message__footer">
              <span v-if="item.status === 'sending'" class="widget-message__sending" role="status" aria-label="发送中" data-testid="widget-pending-status">
                <span class="widget-message__spinner" aria-hidden="true"></span>发送中
              </span>
              <template v-else>
                <span class="widget-message__failed" role="status" data-testid="widget-pending-status">发送失败</span>
                <button
                  v-if="conversationStatus !== 'closed'"
                  aria-label="重试发送"
                  class="widget-retry"
                  data-testid="widget-retry"
                  type="button"
                  @click="onRetry(item.clientMessageId)"
                >
                  重试
                </button>
              </template>
            </div>
          </div>
        </article>
        <article v-if="previewVisible" class="widget-message widget-message--stream widget-bubble--in" data-testid="widget-answer-preview" aria-label="智能客服正在回复">
          <AiAvatar class="widget-message__avatar" />
          <div class="widget-message__content">
            <span class="widget-message__sender">智能客服</span>
            <div class="widget-bubble"><MarkdownMessage :source="previewShown" legacy-tables /></div>
          </div>
        </article>
        <div v-if="suggestions.length" class="widget-suggestions" data-testid="widget-suggestions">
          <button
            v-for="item in suggestions"
            :key="item"
            class="widget-suggestions__item"
            data-testid="widget-suggestion"
            type="button"
            @click="sendSuggestion(item)"
          >{{ item }}</button>
        </div>
        <div v-if="aiResponding || counterpartTyping" class="widget-presence" data-testid="widget-presence" role="status">
          <span class="widget-presence__dots" aria-hidden="true"><i></i><i></i><i></i></span>
          {{ aiResponding ? (aiStatus || '智能客服正在回复…') : '客服正在输入…' }}
        </div>
        <template v-if="state === 'authenticated' && conversationStatus === 'closed'">
          <section v-if="ratingState === 'pending'" class="widget-rating" data-testid="widget-rating" aria-labelledby="widget-rating-title">
            <span id="widget-rating-title" class="widget-rating__title">本次服务您满意吗？</span>
            <div class="widget-rating__stars" role="radiogroup" aria-label="服务评分">
              <button
                v-for="n in 5"
                :key="n"
                type="button"
                role="radio"
                :aria-checked="ratingScore === n"
                :aria-label="`${n} 星`"
                :class="{ 'widget-rating__star--on': n <= ratingScore }"
                class="widget-rating__star"
                data-testid="widget-rating-star"
                @click="ratingScore = n"
              >★</button>
            </div>
            <textarea
              v-if="ratingScore > 0"
              v-model="ratingComment"
              aria-label="评价补充说明（选填）"
              class="widget-rating__comment"
              maxlength="500"
              placeholder="还有什么想对我们说的（选填）"
              rows="2"
            ></textarea>
            <button class="widget-rating__submit" type="button" data-testid="widget-rating-submit" :disabled="ratingScore < 1 || ratingBusy" @click="submitRating">
              {{ ratingBusy ? '提交中…' : '提交评价' }}
            </button>
            <span v-if="ratingError" class="widget-rating__error" role="alert">{{ ratingError }}</span>
          </section>
          <p v-else-if="ratingState === 'rated'" class="widget-rating widget-rating__done" data-testid="widget-rating-done" role="status">
            {{ ratingJustSubmitted ? '感谢您的评价！' : '您已评价' }} {{ '★'.repeat(ratingScore) }}
          </p>
        </template>
      </div>

      <p v-if="conversationStatus === 'ai' || conversationStatus === 'waiting'" class="widget-disclaimer" data-testid="widget-ai-disclaimer">
        内容由 AI 生成，仅供参考，重要事项请以人工客服确认为准
      </p>
      <p v-if="attachmentError" class="widget-alert" data-testid="widget-attachment-error" role="alert">{{ attachmentError }}</p>
      <form v-if="conversationStatus !== 'closed'" class="widget-composer" novalidate @submit.prevent="onSend">
        <textarea
          aria-label="输入消息"
          class="widget-composer__input"
          data-testid="widget-input"
          :disabled="state !== 'authenticated' || waitingForAI"
          :placeholder="waitingForAI ? '智能客服正在回复，请稍候…' : '请输入您的问题…'"
          rows="3"
          :value="draft"
          @input="onInput"
          @blur="props.session.stopTyping()"
          @keydown.enter.exact="onComposerEnter"
          @paste="onPaste"
        ></textarea>
        <div class="widget-composer__bottom">
          <label class="widget-composer__tool" title="添加附件">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 12.5 5.8-5.8a3.2 3.2 0 0 1 4.5 4.5l-7.5 7.5a5 5 0 0 1-7.1-7.1l7.8-7.8" /></svg>
            <input aria-label="选择附件" :accept="ATTACHMENT_ACCEPT" data-testid="widget-file" type="file" multiple :disabled="state !== 'authenticated' || uploading" @change="onFileChange" />
          </label>
          <div v-for="(selectedFile, index) in selectedFiles" :key="index" class="widget-composer__file" data-testid="widget-selected-file">
            <ImageAttachmentPreview v-if="selectedFile.type.startsWith('image/')" :file="selectedFile" />
            <span v-else>{{ selectedFile.name }}</span>
            <button type="button" :disabled="uploading" data-testid="widget-remove-file" @click="removeFile(selectedFile)">移除</button>
          </div>
          <button
            aria-label="发送"
            class="widget-composer__send"
            data-testid="widget-send"
            :disabled="state !== 'authenticated' || uploading || waitingForAI || (draft.trim() === '' && selectedFiles.length === 0)"
            type="button"
            @click="onSend"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m3 11 17-8-5 18-3.5-7L3 11Zm8.5 3L20 3" /></svg>
            发送
          </button>
        </div>
      </form>
    </section>
  </div>
</template>

<style>
/*
 * 客户悬浮窗使用与工作台一致的鲸算盘青色主题。
 * 悬浮窗嵌在宿主页面里，主题随宿主不可控，故固定为浅色，不跟随 prefers-color-scheme。
 */
:root {
  --w-primary: #17a2b8;
  --w-primary-hover: #087b8e;
  --w-primary-soft: #ecf7f9;
  --w-on-primary: #ffffff;
  --w-fg: #111827;                 /* gray-900 标题 */
  --w-fg-body: #374151;            /* gray-700 正文 */
  --w-fg-muted: #6b7280;           /* gray-500 次要 */
  --w-fg-subtle: #9ca3af;          /* gray-400 占位/弱提示 */
  --w-bg: #ffffff;
  --w-surface: #f7f9fc;
  --w-border: #e5e7eb;             /* gray-200 */
  --w-border-strong: #d1d5db;      /* gray-300 输入框边框 */
  --w-danger: #dc2626;             /* red-600 */
  --w-danger-soft: #fef2f2;        /* red-50 */
  --w-danger-border: #fecaca;      /* red-200 */
  --w-radius: 8px;                 /* rounded-lg */
  --w-radius-lg: 12px;             /* rounded-xl */
  --w-press: 0.98;                 /* active:scale-[0.98] */
  --w-lift: -2px;                  /* hover:-translate-y-0.5 */
  --w-dur-fast: 150ms;
  --w-dur-base: 200ms;
  --w-ease: cubic-bezier(0.4, 0, 0.2, 1);
  --w-shadow-sm: 0 1px 2px 0 rgb(17 24 39 / 6%);
  --w-shadow-md: 0 6px 16px -4px rgb(17 24 39 / 14%), 0 2px 6px -2px rgb(17 24 39 / 8%);
  --w-shadow-pop: 0 8px 28px rgb(17 24 39 / 18%);
}

body {
  margin: 0;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: var(--w-fg-body);
}

.widget-root {
  align-items: flex-end;
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  height: 100dvh;
  min-height: 0;
}

/* 焦点环：ring-2 + offset-2，与 elevation 阴影共存（用 outline，不吃 box-shadow）。 */
.widget-root :focus-visible {
  outline: 2px solid var(--w-primary);
  outline-offset: 2px;
}

/* 悬浮球：主色实底、扁平无渐变，hover 上浮 + 阴影微升，active 按压。 */
.widget-launcher {
  align-items: center;
  background: var(--w-primary);
  border: none;
  border-radius: 999px;
  box-shadow: var(--w-shadow-md);
  color: var(--w-on-primary);
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  padding: 10px 16px;
  position: relative;
  transition:
    background-color var(--w-dur-fast) var(--w-ease),
    box-shadow var(--w-dur-fast) var(--w-ease),
    transform var(--w-dur-fast) var(--w-ease);
}

.widget-launcher:hover {
  background: var(--w-primary-hover);
  box-shadow: var(--w-shadow-pop);
  transform: translateY(var(--w-lift));
}

.widget-launcher:active {
  box-shadow: var(--w-shadow-sm);
  transform: translateY(0) scale(var(--w-press));
}

.widget-launcher__icon {
  display: inline-flex;
  font-size: 16px;
  line-height: 1;
}

.widget-launcher__badge {
  background: var(--w-danger);
  border-radius: 999px;
  color: var(--w-on-primary);
  font-size: 12px;
  font-weight: 600;
  min-width: 18px;
  padding: 0 5px;
  text-align: center;
}

/* 面板：rounded-xl + shadow，卡片质感。 */
.widget-panel {
  background: var(--w-bg);
  border: 0;
  border-radius: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  width: 100%;
}

.widget-panel__header {
  align-items: center;
  border-bottom: 1px solid var(--w-border);
  display: flex;
  gap: 8px;
  padding: 12px;
}

.widget-panel__queue {
  background: var(--w-bg-subtle, #f7f8fb);
  border-bottom: 1px solid var(--w-border);
  color: var(--w-fg-muted);
  font-size: 12px;
  margin: 0;
  padding: 8px 12px;
}

.widget-panel__closed {
  align-items: center;
  background: var(--w-primary-soft);
  border-bottom: 1px solid var(--w-border);
  color: var(--w-fg-body);
  display: flex;
  flex: none;
  font-size: 12px;
  gap: 8px;
  justify-content: space-between;
  padding: 10px 12px;
}

.widget-panel__closed-text {
  flex: 1;
  min-width: 0;
  line-height: 1.5;
}

.widget-panel__continue {
  background: var(--w-primary);
  border: 1px solid var(--w-primary);
  border-radius: var(--w-radius);
  color: var(--w-on-primary);
  cursor: pointer;
  flex: none;
  font: inherit;
  font-weight: 600;
  padding: 5px 12px;
  white-space: nowrap;
  transition: background-color var(--w-dur-fast) var(--w-ease);
}

.widget-panel__continue:hover:not(:disabled) {
  background: var(--w-primary-hover);
}

.widget-panel__continue:disabled {
  cursor: wait;
  opacity: 0.6;
}

.widget-panel__heading {
  flex: 1;
}

.widget-panel__title {
  color: var(--w-fg);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0;
}

.widget-panel__meta {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
  font-size: 12px;
}

.widget-panel__status {
  color: var(--w-primary);
  font-weight: 500;
}

/* 次级按钮统一基线：白底 + 灰边 + rounded-lg + shadow-sm，hover 上浮、active 按压。 */
.widget-panel__close,
.widget-panel__handoff,
.widget-retry,
.widget-composer__file button[data-testid="widget-remove-file"],
.widget-bubble ul button {
  background: var(--w-bg);
  border: 1px solid var(--w-border-strong);
  border-radius: var(--w-radius);
  box-shadow: var(--w-shadow-sm);
  color: var(--w-fg-body);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  transition:
    background-color var(--w-dur-fast) var(--w-ease),
    border-color var(--w-dur-fast) var(--w-ease),
    box-shadow var(--w-dur-fast) var(--w-ease),
    transform var(--w-dur-fast) var(--w-ease);
}

.widget-panel__close:hover,
.widget-panel__handoff:hover,
.widget-retry:hover,
.widget-composer__file button[data-testid="widget-remove-file"]:hover,
.widget-bubble ul button:hover {
  border-color: var(--w-primary);
  box-shadow: var(--w-shadow-md);
  color: var(--w-primary);
  transform: translateY(var(--w-lift));
}

.widget-panel__close:active,
.widget-retry:active,
.widget-composer__file button[data-testid="widget-remove-file"]:active,
.widget-bubble ul button:active {
  box-shadow: var(--w-shadow-sm);
  transform: translateY(0) scale(var(--w-press));
}

/* 错误提示：danger 语义（底色 + 边框 + 文字），不只靠颜色单一通道。 */
.widget-alert {
  background: var(--w-danger-soft);
  border-bottom: 1px solid var(--w-danger-border);
  color: var(--w-danger);
  display: flex;
  flex-direction: column;
  font-size: 12px;
  gap: 2px;
  margin: 0;
  padding: 8px 12px;
}

.widget-alert__title {
  font-weight: 600;
}

.widget-panel__body {
  background: var(--w-surface);
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  padding: 12px;
  scrollbar-width: none;
}

.widget-panel__body::-webkit-scrollbar,
.widget-composer__input::-webkit-scrollbar {
  display: none;
}

.widget-empty {
  color: var(--w-fg-muted);
  font-size: 13px;
  margin: auto;
  text-align: center;
}

.widget-resolution {
  box-sizing: border-box;
  font-size: 12px;
  max-width: 100%;
}

.widget-message__footer > .widget-resolution { margin-left: auto; }

.widget-resolution p { margin: 0; line-height: 1.6; }
.widget-resolution__actions { display: flex; align-items: center; gap: 4px; }
.widget-resolution__choice {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--w-fg-muted);
  cursor: pointer;
}
.widget-resolution__choice svg { width: 18px; height: 18px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.widget-resolution__choice:hover,
.widget-resolution__choice[aria-pressed="true"] { color: var(--w-primary); background: var(--w-primary-soft); }
.widget-resolution__thanks { margin-left: 4px; color: var(--w-fg-muted); }
.widget-resolution__handoff { display: grid; justify-items: start; gap: 8px; margin-top: 8px; }
.widget-resolution__error { color: var(--w-danger); }
.widget-resolution button:disabled { cursor: default; opacity: 0.6; transform: none; }
.widget-resolution button:focus-visible { outline: 2px solid var(--w-primary); outline-offset: 2px; }

.widget-history-divider {
  align-items: center;
  color: var(--w-fg-muted);
  display: flex;
  flex: none;
  font-size: 12px;
  gap: 10px;
  margin: 8px 0;
  white-space: nowrap;
}

.widget-history-divider::before,
.widget-history-divider::after {
  background: var(--w-border);
  content: '';
  flex: 1;
  height: 1px;
}

.widget-message {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  max-width: 88%;
}

.widget-message__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.widget-message .widget-message__avatar,
.widget-message__system-avatar {
  flex: none;
  width: 32px;
  height: 32px;
}

.widget-rating {
  box-sizing: border-box;
  display: flex;
  flex: none;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  min-width: 0;
  margin: 4px 0 0;
  padding: 14px;
  border: 1px solid var(--w-border);
  border-radius: var(--w-radius-lg);
  background: var(--w-bg);
}

.widget-rating__title {
  color: var(--w-fg);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
}

.widget-rating__stars {
  display: flex;
  gap: 4px;
}

.widget-rating__star {
  display: grid;
  place-items: center;
  flex: none;
  width: 32px;
  height: 32px;
  background: none;
  border: none;
  border-radius: 6px;
  color: var(--w-border-strong);
  cursor: pointer;
  font-size: 26px;
  line-height: 1;
  padding: 0;
}

.widget-rating__star:hover {
  background: var(--w-primary-soft);
}

.widget-rating__star--on {
  color: #f5a623;
}

.widget-rating__comment {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  background: var(--w-bg);
  color: var(--w-fg-body);
  border: 1px solid var(--w-border-strong);
  border-radius: var(--w-radius);
  font: inherit;
  font-size: 13px;
  line-height: 1.5;
  padding: 8px 10px;
  resize: none;
}

.widget-rating__submit {
  align-self: stretch;
  background: var(--w-primary);
  border: none;
  border-radius: var(--w-radius);
  color: var(--w-on-primary);
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  min-height: 34px;
  padding: 7px 14px;
}

.widget-rating__submit:hover:not(:disabled) {
  background: var(--w-primary-hover);
}

.widget-rating__submit:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.widget-rating__error {
  color: var(--w-danger);
  font-size: 12px;
  line-height: 1.5;
}

.widget-rating__done {
  color: var(--w-fg-muted);
  font-size: 12px;
  line-height: 1.5;
}

.widget-suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: 38px;
}

.widget-suggestions__item {
  background: var(--w-bg);
  border: 1px solid var(--w-primary);
  border-radius: 999px;
  color: var(--w-primary-hover);
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  padding: 4px 10px;
  text-align: left;
}

.widget-suggestions__item:hover {
  background: var(--w-primary-soft);
}

.widget-disclaimer {
  background: var(--w-surface);
  color: var(--w-fg-muted);
  font-size: 11px;
  margin: 0;
  padding: 4px 12px 0;
  text-align: center;
}

.widget-notice {
  align-self: center;
  background: var(--w-border);
  border-radius: 999px;
  color: var(--w-fg-muted);
  font-size: 12px;
  margin: 4px 0;
  max-width: 88%;
  padding: 4px 12px;
  text-align: center;
}

.widget-message__sender {
  color: var(--w-fg-muted);
  font-size: 12px;
  margin-bottom: 2px;
}

.widget-message__system-avatar {
  display: grid;
  place-items: center;
  border: 1px solid var(--w-border);
  border-radius: 50%;
  background: var(--w-primary-soft);
  color: var(--w-fg-muted);
  font-size: 12px;
}

.widget-bubble {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  border-radius: var(--w-radius-lg);
  box-shadow: var(--w-shadow-sm);
  padding: 8px 10px;
}

.widget-bubble--in .widget-bubble {
  align-self: flex-start;
  background: var(--w-bg);
  border: 1px solid var(--w-border);
}

.widget-bubble--out .widget-bubble {
  align-self: flex-end;
  background: var(--w-primary-soft);
  border: 1px solid var(--w-border);
}

.widget-bubble--in {
  align-self: flex-start;
}

.widget-bubble--out {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.widget-bubble--pending {
  opacity: 0.85;
}

/* 流式回复的气泡首次出现时轻轻淡入，避免从“正在回复”提示直接蹦出来。 */
.widget-message--stream {
  animation: widget-stream-in 180ms ease-out;
}

@keyframes widget-stream-in {
  from { opacity: 0; transform: translateY(4px); }
}

.widget-presence {
  align-items: center;
  align-self: flex-start;
  display: inline-flex;
  gap: 8px;
  color: var(--w-fg-muted);
  font-size: 12px;
  padding: 7px 10px;
}

.widget-presence__dots { display: inline-flex; gap: 3px; }
.widget-presence__dots i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
  animation: widget-presence-pulse 1.1s ease-in-out infinite;
}
.widget-presence__dots i:nth-child(2) { animation-delay: 140ms; }
.widget-presence__dots i:nth-child(3) { animation-delay: 280ms; }
@keyframes widget-presence-pulse { 50% { opacity: .35; transform: translateY(-2px); } }
@media (prefers-reduced-motion: reduce) { .widget-presence__dots i { animation: none; } }

.widget-message__footer {
  align-items: center;
  display: flex;
  gap: 6px;
  min-height: 18px;
  padding: 2px 2px 0;
}

.widget-bubble--out .widget-message__footer {
  justify-content: flex-end;
}

.widget-message__time,
.widget-message__receipt,
.widget-message__sending,
.widget-message__failed {
  color: var(--w-fg-subtle);
  font-size: 11px;
  line-height: 16px;
}

.widget-message__time {
  opacity: 0;
  transition: opacity var(--w-dur-fast) var(--w-ease);
}

.widget-message:hover .widget-message__time,
.widget-message:focus-within .widget-message__time {
  opacity: 1;
}

.widget-message__receipt--read {
  color: var(--w-primary);
}

.widget-message__sending {
  align-items: center;
  display: inline-flex;
  gap: 5px;
}

.widget-message__spinner {
  border: 1.5px solid var(--w-border-strong);
  border-right-color: var(--w-primary);
  border-radius: 50%;
  height: 10px;
  width: 10px;
  animation: widget-spin 700ms linear infinite;
}

.widget-message__failed {
  color: var(--w-danger);
}

@keyframes widget-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .widget-message__spinner { animation: none; }
}

.widget-bubble__attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
}

.widget-composer {
  background: var(--w-bg);
  border: 1px solid var(--w-border);
  border-radius: 12px;
  box-shadow: 0 2px 14px rgb(31 35 41 / 5%);
  display: flex;
  flex: none;
  flex-direction: column;
  margin: 12px;
  min-height: 160px;
  overflow: hidden;
  transition: border-color 150ms ease, box-shadow 150ms ease;
}

.widget-composer:focus-within {
  border-color: #b8dfe5;
  box-shadow: 0 0 0 3px rgb(23 162 184 / 8%);
}

.widget-composer__bottom {
  flex-wrap: wrap;
  align-items: center;
  display: flex;
  flex: none;
  gap: 8px;
  min-height: 50px;
  padding: 7px 10px 10px;
}

.widget-composer__tool {
  border-radius: 7px;
  color: #77808d;
  cursor: pointer;
  display: grid;
  flex: none;
  height: 32px;
  overflow: hidden;
  place-items: center;
  position: relative;
  width: 32px;
}

.widget-composer__tool:hover {
  background: var(--w-primary-soft);
  color: var(--w-primary-hover);
}

.widget-composer__tool:has(input:disabled) {
  cursor: not-allowed;
  opacity: .45;
}

.widget-composer__tool svg,
.widget-composer__send svg {
  fill: none;
  height: 18px;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.8;
  width: 18px;
}

.widget-composer__tool input[type='file'] {
  cursor: pointer;
  height: 100%;
  inset: 0;
  opacity: 0;
  position: absolute;
  width: 100%;
}

.widget-composer__file {
  align-items: center;
  background: #f3f5f8;
  border-radius: 6px;
  color: var(--w-fg-body);
  display: flex;
  font-size: 12px;
  gap: 8px;
  max-width: 55%;
  min-width: 0;
  overflow: hidden;
  padding: 4px 8px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-composer__input {
  background: transparent;
  border: 0;
  border-radius: 0;
  color: var(--w-fg);
  box-sizing: border-box;
  flex: 1 1 auto;
  font: inherit;
  font-size: 13px;
  line-height: 1.55;
  min-height: 100px;
  min-width: 0;
  overflow-y: auto;
  padding: 15px 18px 8px;
  resize: none;
  scrollbar-width: none;
  width: 100%;
}

.widget-composer__input:focus {
  outline: none;
}

.widget-composer__input::placeholder {
  color: var(--w-fg-subtle);
}

/* 发送是主操作：primary 实底、扁平、hover 加深 + 上浮、active 按压。 */
.widget-composer__send {
  align-items: center;
  background: var(--w-primary);
  border: none;
  border-radius: var(--w-radius);
  box-shadow: var(--w-shadow-sm);
  color: var(--w-on-primary);
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  font-weight: 500;
  gap: 6px;
  margin-left: auto;
  padding: 7px 16px;
  transition:
    background-color var(--w-dur-fast) var(--w-ease),
    box-shadow var(--w-dur-fast) var(--w-ease),
    transform var(--w-dur-fast) var(--w-ease);
}

.widget-composer__send:hover:not(:disabled) {
  background: var(--w-primary-hover);
  box-shadow: var(--w-shadow-md);
  transform: translateY(var(--w-lift));
}

.widget-composer__send:active:not(:disabled) {
  box-shadow: var(--w-shadow-sm);
  transform: translateY(0) scale(var(--w-press));
}

.widget-composer__send:disabled {
  background: var(--w-border-strong);
  box-shadow: none;
  color: var(--w-fg-subtle);
  cursor: not-allowed;
  transform: none;
}

/* reduced-motion：尊重用户偏好，关闭位移 / 缩放与长过渡。 */
@media (prefers-reduced-motion: reduce) {
  .widget-root *,
  .widget-root *::before,
  .widget-root *::after {
    animation-duration: 1ms !important;
    transition-duration: 1ms !important;
  }

  .widget-launcher:hover,
  .widget-launcher:active,
  .widget-panel__close:hover,
  .widget-retry:hover,
  .widget-composer__send:hover,
  .widget-composer__send:active {
    transform: none !important;
  }
}
</style>
